import {
  Button,
  Select,
  MenuItem,
  TextField,
  FormHelperText,
} from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import FormControl from '@material-ui/core/FormControl';
import IconButton from '@material-ui/core/IconButton';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import { useEffect, useRef, useState } from 'react';

const CONTAINERS = ['Project', 'Subject', 'Session', 'Acquisition'];

const styles = (theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '16px',
    paddingLeft: '24px',
    paddingRight: '24px',
    '& form': {
      paddingBottom: '15px',
      '& :first-child': {
        marginTop: '0',
      },
      '& input, textarea': {
        fontSize: '14px',
        lineHeight: '16px',
      },
      '& #container': {
        fontSize: '14px',
        lineHeight: '16px',
      },
      '& .MuiFormControl-root': {
        marginTop: '0',
        marginBottom: '0.5rem',
        fontSize: '14px',
        lineHeight: '16px',
        '& label': {
          marginTop: '1rem',
        },
      },
      '& .MuiFormHelperText-contained': {
        marginLeft: '0',
      },
    },
    '& label': {
      width: '100%',
      'font-weight': '500',
      'font-size': '12px',
      'line-height': '14px',
      color: '#333333',
      marginTop: '1rem',
    },
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  palette: {
    primary: {
      main: '#2e72f6',
      contrastText: '#fff',
    },
  },
});

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const CreateNewFormDialog = withStyles(styles)(({classes, data, mode, onClose, onSave, open}) => {
  const formRef = useRef();
  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [description, setDescription] = useState('');
  const [container, setContainer] = useState('');
  const [error, setError] = useState({});
  const [touched, setTouched] = useState({});
  const DEFAULT_FORM_DEF = {
    display: 'form',
    components: [],
  };
  const reset = () => {
    setName('');
    setKey('');
    setDescription('');
    setContainer('');

    setTouched({});
    setError({});
  };

  const handleTouch = (e) => {
    const fieldName = e.target.name;
    const updateTouchValue = Object.assign({}, touched, { [fieldName]: true });
    setTouched(updateTouchValue);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSave = async () => {
    const form = Object.assign({}, DEFAULT_FORM_DEF, data?.form || {}, {
      key,
      name,
      description,
    });

    await onSave({
      container, form, key
    });

    reset();
    onClose(
      mode === 'create'
        ? {
            containerType: container,
            key,
          }
        : null
    );
  };

  useEffect(() => {
    let error = {};

    if (!name.length) {
      error.name = 'Form Name is required';
    }
    if (!key.length) {
      error.key = 'Form Key is required';
    }
    if (!container.length) {
      error.container = 'Applies To is required';
    }
    setError(error);
  }, [name, key, container, open]);

  useEffect(() => {
    const { containerType, key, form } = data || {};

    if (containerType) {
      setContainer(containerType);
    }
    if (key) {
      setKey(key);
    }
    if (form) {
      setName(form.name || '');
      setDescription(form.description || '');
    }
  }, [data]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableBackdropClick
      className={classes.root}
    >
      <DialogTitle onClose={handleClose}>
        {mode === 'create' ? 'Create New' : 'Edit'} Form
      </DialogTitle>
      <DialogContent dividers>
        <form className="create-new-form" ref={formRef}>
          <label htmlFor="name">
            Form Name<span className="required">*</span>
          </label>
          <TextField
            id="name"
            name="name"
            fullWidth
            required
            error={touched.name && Boolean(error.name)}
            margin="dense"
            variant="outlined"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setKey(
                touched.key
                  ? key
                  : e.target.value.replace(/ /g, '_').toLowerCase()
              );
            }}
            onFocus={handleTouch}
            helperText={touched.name && error.name}
          ></TextField>
          <label htmlFor="key">
            Form Key<span className="required">*</span>
          </label>
          <TextField
            id="key"
            name="key"
            fullWidth
            required
            error={touched.key && Boolean(error.key)}
            margin="dense"
            variant="outlined"
            value={key}
            onChange={(e) => {
              setKey(e.target.value);
            }}
            onFocus={handleTouch}
            helperText={touched.key && error.key}
          ></TextField>
          <label htmlFor="description">Form Description</label>
          <TextField
            id="description"
            name="description"
            multiline
            fullWidth
            rows={4}
            margin="dense"
            variant="outlined"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          ></TextField>

          <FormControl
            fullWidth
            required
            error={touched.container && Boolean(error.container)}
            onFocus={handleTouch}
            onBlur={handleTouch}
            placeholder="Select a hierarchy level"
          >
            <label htmlFor="container">
              Applies To<span className="required">*</span>
            </label>
            <Select
              id="container"
              name="container"
              variant="outlined"
              value={container}
              margin="dense"
              onChange={(e) => {
                setContainer(e.target.value);
              }}
              onFocus={handleTouch}
              onBlur={handleTouch}
              SelectDisplayProps={{
                id: 'container',
              }}
              placeholder="Select a hierarchy level"
              aria-describedby="container-error-text"
            >
              <MenuItem value="" disabled>
                Select hierarchy level
              </MenuItem>
              {CONTAINERS.map((level, index) => {
                return (
                  <MenuItem key={index} value={level.toLowerCase()}>
                    {level}
                  </MenuItem>
                );
              })}
            </Select>
            <FormHelperText
              id="container-error-text"
              style={{
                display:
                  touched.container && error.container ? 'block' : 'none',
              }}
            >
              Applies To is required
            </FormHelperText>
          </FormControl>
        </form>
      </DialogContent>
      <DialogActions>
        <Button
          autoFocus
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={
            Object.keys(error).filter((v) => error[v] !== null).length > 0
          }
        >
          {mode === 'create' ? 'Create' : 'Save'} Form
        </Button>
        <Button onClick={handleClose} variant="contained" color="secondary">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export { CreateNewFormDialog };
